import React from 'react';
import { Box, Chip, ChipProps } from '@mui/material';
import { SignalTone, tokens } from '../../theme/tokens';

/**
 * The one status vocabulary. Pages used to speak five different ones
 * (`success|warning|error`, `blocking|warning|positive`, `ok|warn|bad`, a
 * selected boolean, English tag names); every chip now maps to these five.
 * The MUI names are accepted as aliases so existing callers keep working.
 */
export type StatusTone =
  | SignalTone
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'primary'
  | 'secondary'
  | 'default';

const ALIASES: Record<StatusTone, SignalTone> = {
  positive: 'positive',
  caution: 'caution',
  problem: 'problem',
  info: 'info',
  neutral: 'neutral',
  success: 'positive',
  warning: 'caution',
  error: 'problem',
  primary: 'info',
  secondary: 'info',
  default: 'neutral',
};

/** Unknown values (a page passing a raw MUI colour) fall back to neutral rather than crashing. */
export const toSignalTone = (tone: StatusTone | string | undefined): SignalTone =>
  ALIASES[tone as StatusTone] ?? 'neutral';

const POSITIVE = /^(active|success|succeeded|ok|done|completed?|complete|healthy|approved|processed|connected|enabled|paid|live|passed|ready|synced|verified)$/i;
const CAUTION = /^(pending|warning|warn|partial|review|queued|paused|flapping|degraded|stale|awaiting|trial|past_due|pastdue|upcoming|draft|suspended)$/i;
const PROBLEM = /^(error|failed|failing|failure|rejected|blocking|blocked|cancell?ed|denied|revoked|critical|bad|disabled|locked|overdue|violation)$/i;
const INFO = /^(running|scheduled|in_progress|inprogress|processing|syncing|info|new|open)$/i;

/**
 * Best-effort tone for a free-text status value (`'pending'`, `'FAILED'`,
 * `'in_progress'`). Unknown words are neutral. Use it to replace a page's
 * hand-written `getStatusChip` switch.
 */
export const statusTone = (status: string | null | undefined): SignalTone => {
  const value = (status ?? '').trim();
  if (POSITIVE.test(value)) return 'positive';
  if (CAUTION.test(value)) return 'caution';
  if (PROBLEM.test(value)) return 'problem';
  if (INFO.test(value)) return 'info';
  return 'neutral';
};

/** Title-case a raw status key for display: `in_progress` becomes `In progress`. */
export const statusLabel = (status: string | null | undefined): string => {
  const value = (status ?? '').replace(/[_-]+/g, ' ').trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : 'Unknown';
};

export interface StatusChipProps {
  label: React.ReactNode;
  tone?: StatusTone;
  size?: ChipProps['size'];
  /** `soft` is a tinted fill (default); `outlined` is a hairline on white. */
  variant?: 'soft' | 'outlined';
  /** Leading tone-coloured dot, for dense tables where colour alone is too quiet. */
  dot?: boolean;
  icon?: ChipProps['icon'];
  onClick?: ChipProps['onClick'];
  sx?: ChipProps['sx'];
}

export const StatusChip: React.FC<StatusChipProps> = ({
  label,
  tone = 'neutral',
  size = 'small',
  variant = 'soft',
  dot = false,
  icon,
  onClick,
  sx,
}) => {
  const palette = tokens.signal[toSignalTone(tone)];
  return (
    <Chip
      label={
        dot ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              component="span"
              sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: palette.solid, flexShrink: 0 }}
            />
            {label}
          </Box>
        ) : (
          label
        )
      }
      size={size}
      icon={icon}
      onClick={onClick}
      sx={{
        color: palette.ink,
        bgcolor: variant === 'soft' ? palette.tint : 'transparent',
        border: `1px solid ${palette.border}`,
        borderRadius: `${tokens.radius.chip}px`,
        '& .MuiChip-icon': { color: palette.ink },
        ...sx,
      }}
    />
  );
};
